import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import { OtpInput } from '@/components/ui/OtpInput';
import { Button } from '@/components/ui/Button';
import { HeroSection } from '@/components/common';
import { generateAllOtp, verifyRegisterOtp } from '@/services/authApi';
import { getDeviceInfo } from '@/utils/deviceInfo';
import { AUTH_ROUTES } from '@/constants/routes/authRoutes';
import type { GenerateAllOtpResponse, OtpTransactionInfo } from '@/types/auth.types';

import './OtpVerification.css';
import { TEXT } from '@/constants/static/registerLabels';

interface OtpLocationState {
  email: string;
  mobileNumber: string;
  emailTransactionId: string;
  mobileTransactionId: string;
}

const extractTransactionIds = (
  data: GenerateAllOtpResponse['data']
): { emailTransactionId: string; mobileTransactionId: string } | null => {
  if (!data) return null;

  if (Array.isArray(data)) {
    const emailTx = data.find((item: OtpTransactionInfo) => item.deliveryMethod === 'EMAIL');
    const mobileTx = data.find((item: OtpTransactionInfo) => item.deliveryMethod === 'SMS');
    if (emailTx?.transactionId && mobileTx?.transactionId) {
      return {
        emailTransactionId: emailTx.transactionId,
        mobileTransactionId: mobileTx.transactionId,
      };
    }
    return null;
  }

  if (data.emailTransactionId && data.mobileTransactionId) {
    return {
      emailTransactionId: data.emailTransactionId,
      mobileTransactionId: data.mobileTransactionId,
    };
  }

  return null;
};

const OTP_LENGTH = 6;
const INITIAL_TIMER = 30;
const MAX_ATTEMPTS = 3;

const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (!domain || localPart.length <= 3) return email;
  return `${localPart.slice(0, 2)}${'*'.repeat(localPart.length - 3)}${localPart.slice(-1)}@${domain}`;
};

const maskMobile = (mobileNumber: string): string => {
  const digits = mobileNumber.replace(/\D/g, '');
  const lastFive = digits.slice(-5);
  const maskedPrefix = 'X'.repeat(Math.max(0, digits.length - lastFive.length));
  return `+91 ${maskedPrefix}${lastFive}`;
};

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = (seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${secs}`;
};

const OtpVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as OtpLocationState | null) ?? null;

  const [emailOtp, setEmailOtp] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [emailTransactionId, setEmailTransactionId] = useState(state?.emailTransactionId ?? '');
  const [mobileTransactionId, setMobileTransactionId] = useState(state?.mobileTransactionId ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(INITIAL_TIMER);
  const [attempts, setAttempts] = useState(0);

  const email = state?.email ?? '';
  const mobileNumber = state?.mobileNumber ?? '';

  useEffect(() => {
    if (!state || !email || !mobileNumber || !emailTransactionId || !mobileTransactionId) {
      navigate('/register', { replace: true });
    }
  }, [state, email, mobileNumber, emailTransactionId, mobileTransactionId, navigate]);

  useEffect(() => {
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (!success) return;

    // console.log('[OtpVerification] Success effect triggered; navigating to login in 2s');
    const timeout = setTimeout(() => {
      // console.log('[OtpVerification] Navigating to login');
      navigate(AUTH_ROUTES.LOGIN, { replace: true });
    }, 2000);

    return () => clearTimeout(timeout);
  }, [success, navigate]);

  const canResend = timer === 0;
  const isOtpComplete = useMemo(
    () => emailOtp.length === OTP_LENGTH && mobileOtp.length === OTP_LENGTH,
    [emailOtp, mobileOtp]
  );
  const isMaxAttemptsReached = attempts >= MAX_ATTEMPTS;

  // Auto-verify once both codes are complete — no need to press the button.
  const handleVerifyRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (isOtpComplete && !isLoading && !isMaxAttemptsReached && !success) {
      handleVerifyRef.current();
    }
  }, [isOtpComplete, isLoading, isMaxAttemptsReached, success]);

  const handleResend = async () => {
    if (!canResend || !email || !mobileNumber) return;

    setIsLoading(true);
    setError(null);

    try {
      const response: GenerateAllOtpResponse = await generateAllOtp({
        email,
        mobileNumber,
        deliveryMethod: 'EMAIL',
      });

      const transactions = extractTransactionIds(response.data);
      if (transactions) {
        setEmailTransactionId(transactions.emailTransactionId);
        setMobileTransactionId(transactions.mobileTransactionId);
      }

      setEmailOtp('');
      setMobileOtp('');
      setAttempts(0);
      setTimer(INITIAL_TIMER);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resend OTP. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (err: unknown, fallback: string): string => {
    if (err instanceof Error) return err.message;
    return fallback;
  };

  const handleVerify = async () => {
    if (!isOtpComplete || isMaxAttemptsReached) {
      // console.log('[OtpVerification] Verify blocked', { isOtpComplete, isMaxAttemptsReached });
      return;
    }

    setIsLoading(true);
    setError(null);

    const deviceInfo = getDeviceInfo();

    try {
      // console.log('[OtpVerification] Verifying email OTP', { transactionId: emailTransactionId, code: emailOtp });
      const emailResponse = await verifyRegisterOtp({
        transactionId: emailTransactionId,
        code: emailOtp,
        deliveryMethod: 'EMAIL',
        deviceInfo,
      });
      // console.log('[OtpVerification] Email OTP response', emailResponse);

      if (emailResponse.success === false) {
        throw new Error(emailResponse.message || 'Invalid Email OTP');
      }
    } catch (err) {
      const message = getErrorMessage(err, 'Invalid Email OTP');
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);

      if (message.toLowerCase().includes('expired')) {
        setError('OTP expired. Please request a new OTP.');
      } else if (nextAttempts >= MAX_ATTEMPTS) {
        setError('Maximum OTP attempts reached. Please resend OTP.');
      } else {
        setError('Invalid Email OTP');
      }

      setIsLoading(false);
      return;
    }

    try {
      // console.log('[OtpVerification] Verifying mobile OTP', { transactionId: mobileTransactionId, code: mobileOtp });
      const mobileResponse = await verifyRegisterOtp({
        transactionId: mobileTransactionId,
        code: mobileOtp,
        deliveryMethod: 'SMS',
        deviceInfo,
      });
      // console.log('[OtpVerification] Mobile OTP response', mobileResponse);

      if (mobileResponse.success === false) {
        throw new Error(mobileResponse.message || 'Invalid Mobile OTP');
      }

      // console.log('[OtpVerification] Both OTPs verified; setting success');
      setSuccess(true);
    } catch (err) {
      const message = getErrorMessage(err, 'Invalid Mobile OTP');
      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);

      if (message.toLowerCase().includes('expired')) {
        setError('OTP expired. Please request a new OTP.');
      } else if (nextAttempts >= MAX_ATTEMPTS) {
        setError('Maximum OTP attempts reached. Please resend OTP.');
      } else {
        setError('Invalid Mobile OTP');
      }
    } finally {
      setIsLoading(false);
    }
  };

  handleVerifyRef.current = () => {
    void handleVerify();
  };

  return (
    <div className="nova-register-page">
      <div className="nova-register-container">
        <HeroSection />

        {/* Right OTP Section */}
        <div className="nova-form-section">
          <div className="nova-form-card">
            {success ? (
              <div className="nova-otp-success-body">
                <div className="nova-otp-success-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <h2 className="nova-otp-success-title">{TEXT.otpVerification.otpSuccessTitle}</h2>
                <p className="nova-otp-success-message">
                  {TEXT.otpVerification.otpSuccessMessage}
                </p>
              </div>
            ) : (
              <>
                <div className="nova-form-header">
                  <h2 className="nova-form-title">{TEXT.otpVerification.formTitle}</h2>
                  <p className="nova-form-subtitle">
                    {TEXT.otpVerification.formSubTtitle}
                  </p>
                </div>

                {error && (
                  <Alert variant="danger" className="nova-alert" dismissible onClose={() => setError(null)}>
                    {error}
                  </Alert>
                )}

                <div className="nova-otp-info-list">
                  <div className="nova-otp-info-row">
                    <span className="nova-otp-info-label">{TEXT.otpVerification.emailInfo.emailLabel}</span>
                    <span className="nova-otp-info-value">{maskEmail(email)}</span>
                  </div>
                  <div className="nova-otp-info-row">
                    <span className="nova-otp-info-label">{TEXT.otpVerification.smsInfo.smsLabel}</span>
                    <span className="nova-otp-info-value">{maskMobile(mobileNumber)}</span>
                  </div>
                </div>

                <div className="nova-otp-group">
                  <label htmlFor="email-otp" className="nova-otp-label">
                    {TEXT.otpVerification.emailInfo.emailOtpLabel}
                  </label>
                  <OtpInput
                    id="email-otp"
                    value={emailOtp}
                    onChange={setEmailOtp}
                    length={OTP_LENGTH}
                    disabled={isLoading || isMaxAttemptsReached}
                    hasError={error?.toLowerCase().includes('email') ?? false}
                    autoComplete="one-time-code"
                  />
                </div>

                <div className="nova-otp-group">
                  <label htmlFor="mobile-otp" className="nova-otp-label">
                    {TEXT.otpVerification.smsInfo.smsOtpLabel}
                  </label>
                  <OtpInput
                    id="mobile-otp"
                    value={mobileOtp}
                    onChange={setMobileOtp}
                    length={OTP_LENGTH}
                    disabled={isLoading || isMaxAttemptsReached}
                    hasError={error?.toLowerCase().includes('mobile') ?? false}
                    autoComplete="one-time-code"
                  />
                </div>

                <div className="nova-otp-resend">
                  {canResend ? (
                    <>
                     {TEXT.otpVerification.resendText}
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={handleResend}
                        disabled={isLoading}
                      >
                       {TEXT.otpVerification.resendBtn}
                      </button>
                    </>
                  ) : (
                    <span className="nova-otp-timer">Resend OTP in {formatTime(timer)}</span>
                  )}
                </div>

                <div className="d-grid">
                  <Button
                    type="button"
                    onClick={handleVerify}
                    isLoading={isLoading}
                    disabled={!isOtpComplete || isMaxAttemptsReached}
                  >
                  {TEXT.otpVerification.verifyBtn}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
